import { TabsWithRoutes } from "@components/common/tabs/tabs-with-routes";
import { ProjectTabs } from "@router/route-params";
import { useAppParams } from "@router/router-helper";
import { NotFoundPage } from "@pages/not-found-page";
import { ProjectOverview } from "@pages/project-details/project-overview/project-overview";
import { ProjectOverviewActionButtons } from "@pages/project-details/project-overview/project-overview-action-buttons";
import { useAppDispatch, useAppSelector } from "@store/store-helper";
import {
  fetchingProjectsFlagsSelector,
  isProjectArchiveSelector,
  selectedProjectContextSelector,
  selectedProjectSelector,
  shouldDisplayMarkupsForViewersSelector,
} from "@store/projects/projects-selector";
import { useEffect } from "react";
import {
  fetchProjectContext,
  fetchProjectDetails,
  fetchProjectSettings,
} from "@store/projects/projects-slice-thunk";
import { useCoreApiClient } from "src/api/use-core-api-client";
import { ProjectTeam } from "@pages/project-details/project-team/project-team";
import { ProjectBackgroundTasks } from "@pages/project-details/project-bg-tasks/project-background-tasks";
import { ProjectSettings } from "@pages/project-details/project-settings/project-settings";
import WarningIcon from "@assets/icons/new/warning_60px.svg?react";
import { OpenProjectButton } from "@pages/project-details/open-project-button";
import { ProjectBackgroundTasksSkeleton } from "@pages/project-details/project-bg-tasks/project-background-tasks-skeleton";
import { DataManagementButton } from "@pages/data-management/data-management-button";
import { ProjectMarkups } from "@pages/project-details/project-markups/project-markups";
import { EmptyPage } from "@components/common/empty-page/empty-page";
import { contactSupport } from "@utils/email-utils";
import { ContactSupportLocations } from "@custom-types/types";
import { FaroButton } from "@components/common/faro-button";
import { useTrackEvent } from "@utils/track-event/use-track-event";
import { ProjectData } from "@pages/project-details/project-data/project-data";
import {
  isBetaTestingEnabledSelector,
  isDevModeEnabledSelector,
} from "@store/app/app-selector";
import { getProjectApiClient } from "@api/project-api/project-api-utils";
import { OpenTabEvents } from "@utils/track-event/track-event-list";
import { ProjectIntegrations } from "@pages/project-details/project-integrations/project-integrations";
import { RequiredPermissionProjectLevelName } from "@utils/permission-control/project-permission-control-types";
import { useHasUserValidPermissionProjectLevel } from "@hooks/permission-control/use-has-user-valid-permission-project-level";
import { resetSelectedProject } from "@store/projects/projects-slice";
import { clearStore } from "@faro-lotv/project-source";
import { resetSdbBackgroundTasksState } from "@store/sdb-background-tasks/sdb-background-tasks-slice";
import { shouldHideOpenProject } from "@utils/project-utils";
import { useHasUserValidFeatureCompanyLevel } from "@hooks/feature-control/use-has-user-valid-feature-company-level";
import { Stack, Typography } from "@mui/material";
import { FaroBetaBadge } from "@components/common/faro-beta-badge";

/**
 * Display the details of the selected project
 */
export function ProjectDetails(): JSX.Element | null {
  const { companyId, projectId, projectTabs } = useAppParams();
  const coreApiClient = useCoreApiClient();
  const { trackEvent } = useTrackEvent();
  const dispatch = useAppDispatch();
  const selectedProject = useAppSelector(selectedProjectSelector);
  const isProjectArchived = useAppSelector(
    isProjectArchiveSelector(projectId ?? "")
  );
  const {
    isFetchingProjects,
    isFetchingProjectContext,
    isFetchingProjectSettings,
  } = useAppSelector(fetchingProjectsFlagsSelector);
  const selectedProjectContext = useAppSelector(selectedProjectContextSelector);
  const isBetaTestingEnabled = useAppSelector(isBetaTestingEnabledSelector);
  const shouldDisplayMarkupsForViewers = useAppSelector(
    shouldDisplayMarkupsForViewersSelector
  );
  const isDevModeEnabled = useAppSelector(isDevModeEnabledSelector);

  const { canViewCompanyMembers } = useHasUserValidFeatureCompanyLevel();
  const { canViewAllProjectMembers, canViewProjectSettings } =
    useHasUserValidPermissionProjectLevel();

  /** Whether the required data to display the project details page is loading */
  const isLoading =
    isFetchingProjects || isFetchingProjectContext || isFetchingProjectSettings;

  /**
   * Fetches the required data to display the project details:
   * - project details
   * - project context
   * - project settings
   */
  useEffect(() => {
    if (projectId) {
      const projectApiClient = getProjectApiClient({
        projectId,
      });

      if (canViewProjectSettings) {
        void dispatch(fetchProjectSettings({ projectApiClient }));
      }

      void dispatch(fetchProjectContext({ projectId }));

      if (companyId) {
        void dispatch(
          fetchProjectDetails({ coreApiClient, companyId, projectId })
        );
      }
    }
  }, [canViewProjectSettings, companyId, coreApiClient, dispatch, projectId]);

  // Resets all store state associated to the selected project:
  // - Selected project from the projects slice.
  // - IElements slice since it should only store data of the selected project
  // - Progress API tasks slice since it should only store data of the selected project.
  useEffect(() => {
    return () => {
      dispatch(resetSelectedProject());
      dispatch(clearStore());
      dispatch(resetSdbBackgroundTasksState());
    };
  }, [dispatch]);

  /**
   * Early exit to the "Not found" page if the requires URL route params are not defined or their value is not valid:
   * - companyId
   * - projectId
   * - projectTabs
   */
  if (
    !companyId ||
    !projectId ||
    !projectTabs ||
    !Object.values(ProjectTabs).includes(projectTabs)
  ) {
    return <NotFoundPage />;
  }

  if (projectTabs === ProjectTabs.evaluation) {
    return (
      <EmptyPage
        title="The feature is no longer supported"
        subtitle="The feature you are looking for is no longer supported in Sphere XG"
        icon={WarningIcon}
        button={
          <FaroButton
            onClick={() =>
              contactSupport({
                location:
                  ContactSupportLocations.projectEvaluationDeprecatedPage,
                trackEvent,
              })
            }
          >
            Contact support
          </FaroButton>
        }
      />
    );
  }

  /**
   * Early exit to the "Not found" page if after fetching the required data to show the project details
   * one data element is not defined:
   * - project details
   * - project context
   * - project settings
   */
  if (
    (selectedProject === null || selectedProjectContext === null) &&
    !isLoading
  ) {
    return <NotFoundPage />;
  }

  /** Shows a button to open the project in Sphere Viewer */
  const projectButtons = selectedProject ? (
    <>
      {!isLoading && selectedProject && (
        <ProjectOverviewActionButtons project={selectedProject} />
      )}
      <DataManagementButton project={selectedProject} isLoading={isLoading} />

      {!shouldHideOpenProject(selectedProject) && (
        <OpenProjectButton project={selectedProject} isLoading={isLoading} />
      )}
    </>
  ) : undefined;

  /**
   * Hide the members tab if
   * - selected project is not yet available to check access permission.
   * - the user has no workspace or project permission to view project members
   */
  const shouldHideMembersTab =
    !selectedProject || !(canViewAllProjectMembers || canViewCompanyMembers);

  return (
    <TabsWithRoutes
      selectedTab={projectTabs}
      selectedProject={selectedProject}
      requiredAttribute={selectedProject}
      trackingEventPageName={OpenTabEvents.openProjectTab}
      isLoading={isLoading}
      tabs={[
        {
          label: "Overview",
          route: ProjectTabs.overview,
          content: (project) => (
            <ProjectOverview project={project} isLoading={isLoading} />
          ),
          loadingContent: <ProjectOverview isLoading={true} />,
          actionButtons: projectButtons,
        },
        {
          label: "Members",
          route: ProjectTabs.members,
          content: (project) => (
            <ProjectTeam
              project={project}
              companyId={companyId}
              isLoading={isLoading}
            />
          ),
          loadingContent: <ProjectTeam isLoading={true} />,
          actionButtons: projectButtons,
          isHidden: shouldHideMembersTab,
        },
        {
          label: "Data",
          route: ProjectTabs.data,
          content: (project) => <ProjectData projectId={project.id} />,
          loadingContent: null,
          actionButtons: projectButtons,
          requiredPermissionProjectLevel:
            RequiredPermissionProjectLevelName.canViewProjectData,
          isHidden: !isBetaTestingEnabled,
        },
        {
          label: "Activity",
          route: ProjectTabs.activity,
          content: (project) => (
            <ProjectBackgroundTasks projectId={project.id} />
          ),
          loadingContent: <ProjectBackgroundTasksSkeleton />,
          actionButtons: projectButtons,
          requiredPermissionProjectLevel: isDevModeEnabled
            ? undefined
            : RequiredPermissionProjectLevelName.canViewProjectCloudActivity,
        },
        {
          label: "Annotations",
          route: ProjectTabs.annotations,
          content: (project) => <ProjectMarkups projectId={project.id} />,
          loadingContent: <div />,
          actionButtons: projectButtons,
          requiredPermissionProjectLevel: shouldDisplayMarkupsForViewers
            ? RequiredPermissionProjectLevelName.canViewProjectMarkupsAllowViewers
            : RequiredPermissionProjectLevelName.canViewProjectMarkups,
        },
        {
          label: "Integrations",
          route: ProjectTabs.integrations,
          content: (project) => <ProjectIntegrations projectId={project.id} />,
          loadingContent: <div />,
          actionButtons: projectButtons,
          requiredPermissionProjectLevel:
            RequiredPermissionProjectLevelName.canViewProjectIntegrations,
          isHidden: isProjectArchived,
          isBetaFeature: true,
          popover: {
            title: (
              <Stack flexDirection={"row"} gap={"8px"}>
                Integrations
                <FaroBetaBadge />
              </Stack>
            ),
            description: (
              <Typography fontSize={"12px"} fontWeight={"400"}>
                Connect Sphere XG with third-party integrations to sync your
                annotations from Sphere XG to external tools. This feature is in
                beta and may evolve based on your feedback.
              </Typography>
            ),
            shouldShowFeedbackButton: false,
          },
        },
        {
          label: "Settings",
          route: ProjectTabs.settings,
          content: (project) => (
            <ProjectSettings projectId={project.id} isLoading={isLoading} />
          ),
          loadingContent: <ProjectSettings isLoading={true} />,
          actionButtons: projectButtons,
          requiredPermissionProjectLevel:
            RequiredPermissionProjectLevelName.canViewProjectSettings,
        },
      ]}
    />
  );
}
